<template>
  <div
    v-tooltip.permanent="tooltip"
    :data-cell="isCell"
    :data-edit-cell="editCell || undefined"
    :data-checked="isDataChecked"
    :data-column="columnName"
    :data-copy-value="copyValue"
    class="ui-grid__cell"
    :class="mainClasses"
    @click="handleClick"
    @dblclick="handleDblClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <RowCellActions
      v-if="isActionsVisible"
      :items="rowActions"
      :item
      @mouseup.stop
      @mousedown.stop
    >
      <slot name="actions" v-bind="{ item }" :size="'sm' as ButtonSize" />
    </RowCellActions>
    <RowCellMenu v-if="isMenuVisible" @click="handleClickMenu" />
    <div v-if="isCaptionVisible" class="ui-grid__caption">
      {{ displayCaption }}:&nbsp;
    </div>
    <div ref="valueRef" class="ui-grid__cell-value" :class="valueClasses" @click="handleClickValue">
      <slot
        v-bind="{
          active,
          columnName,
          displayValue,
          editCell,
          editRow,
          item,
          size: 'sm' as InputSize,
        }"
      >
        {{ displayValue || '&nbsp;' }}
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import {
  ComputedRef,
  Ref,
  computed,
  inject,
  ref,
  useSlots,
  useTemplateRef,
} from 'vue'
import { get } from 'lodash'
import { useResizeObserver } from '@vueuse/core'

import { ButtonSize, DottedMenuItem, InputSize } from '@types'
import {
  GridColumn,
  GridItem,
  GridValueGetter,
  GridValuePaster,
  GridValueCopier,
} from '../utils/types'

import RowCellMenu from './RowCellMenu.vue'
import RowCellActions from './RowCellActions.vue'

type Props = {
  column?: GridColumn
  item: T

  active?: boolean
  editRow?: boolean
  editCell?: boolean

  isFirst?: boolean
  isLast?: boolean

  isCollapsed?: boolean

  rowActions?: DottedMenuItem[]
}

type Emits = {
  click: []
  dblclick: []
  'click:menu': []
  'click:value': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'GridRowCell',
})

defineExpose({
  paste(data: string | number, check = true) {
    if (!columnName.value) return
    valuePaster?.(columnName.value, props.item, data, check)
  },
})

const slots = useSlots()

const disabled = inject('disabled', false)
const hasRowMenu = inject<ComputedRef<boolean>>('hasRowMenu')
const isHovered = inject<Ref<boolean>>('isHovered')
const isChecked = inject<ComputedRef<boolean>>('isChecked')
const valueGetter = inject<GridValueGetter>('valueGetter')
const valuePaster = inject<GridValuePaster>('valuePaster')
const valueCopier = inject<GridValueCopier>('valueCopier')

const valueRef = useTemplateRef('valueRef')
const isValueEllipsis = ref(false)

const columnName = computed(() => props.column?.name)
const displayCaption = computed(() => props.column?.caption)

const copyValue = computed(() => {
  return (props.item && columnName.value && valueCopier?.(columnName.value, props.item)) || undefined
})

const value = computed(() => {
  return (
    props.item &&
    columnName.value &&
    (valueGetter?.(columnName.value, props.item) ||
      get(props.item, columnName.value.split('.')))
  )
})

const displayValue = computed(() => {
  if (!props.item || !props.column?.formatter) return value.value
  const { formatter } = props.column
  return formatter(value.value, props.item as GridItem, props.isCollapsed)
})

const isCell = computed(() =>
  disabled || !props.column || props.column.silent || props.editRow
    ? undefined
    : 'true',
)
const isDataChecked = computed(() =>
  !isCell.value || !isChecked?.value ? undefined : 'true',
)

const isEditing = computed(() => props.editRow || props.editCell)

const mainClasses = computed(() => ({
  'ui-grid__cell--first': !props.isCollapsed && props.isFirst,
  'ui-grid__cell--last': !props.isCollapsed && props.isLast,
  'ui-grid__cell--hovered': !props.isCollapsed && !disabled && isHovered?.value,
  'ui-grid__cell--highlighted':
    !props.isCollapsed && props.active && !props.editRow,
  'ui-grid__cell--checkbox': !props.column,
  [`${props.column?.cellClasses}`]: props.column?.cellClasses,
}))

const valueClasses = computed(() => ({
  'ui-grid__cell-value--truncated':
    props.column && !props.column?.lineClamp && !isEditing.value,
  'ui-grid__cell-value--edit': props.editCell,
  [`line-clamp-${props.column?.lineClamp}`]:
    props.column?.lineClamp && !isEditing.value,
  [`${props.column?.cellValueClasses}`]: props.column?.cellValueClasses,
  'ui-grid__cell-value--clickable': props.column?.clickableValue,
}))

const isMenuVisible = computed(
  () =>
    hasRowMenu?.value &&
    !props.editRow &&
    ((!props.isCollapsed && props.isFirst) ||
      (props.isCollapsed && props.isLast)),
)

const isActionsVisible = computed(() => {
  return (
    slots.actions &&
    !props.editRow &&
    ((!props.isCollapsed && props.isFirst) ||
      (props.isCollapsed && props.isLast))
  )
})

const isCaptionVisible = computed(
  () =>
    props.column && props.isCollapsed && !props.column.hideCaptionIfCollapsed,
)

const tooltip = computed(() => {
  let content
  if (props.column?.tooltip && columnName.value) {
    content = props.column.tooltip(columnName.value, props.item)
  } else if (isValueEllipsis.value) {
    content = displayValue.value
  }
  if (Array.isArray(content)) {
    content = content.join('<br />')
  }
  if (!content) return
  return {
    content,
    handleResize: true,
    html: true,
    placement: tooltipPlacement.value,
  }
})

const tooltipPlacement = computed(
  () => props.column?.tooltipPlacement || 'bottom',
)

const handleDblClick = () => {
  emit('dblclick')
}

const handleClick = () => {
  emit('click')
}

const handleClickMenu = () => {
  emit('click:menu')
}

const handleClickValue = (event: MouseEvent) => {
  if (!props.column?.clickableValue) return
  event.stopPropagation()
  emit('click:value')
}

const handleMouseEnter = () => {
  isHovered && (isHovered.value = true)
}

const handleMouseLeave = () => {
  isHovered && (isHovered.value = false)
}

useResizeObserver(valueRef, () => {
  if (!valueRef.value || isEditing.value) return
  isValueEllipsis.value =
    !props.isCollapsed &&
    (valueRef.value.offsetHeight < valueRef.value.scrollHeight ||
      valueRef.value.offsetWidth < valueRef.value.scrollWidth)
})
</script>

<style>
.ui-grid__cell {
  @apply relative;
  @apply flex items-center;
  @apply relative;
  @apply px-4 py-1;

  &:not(.ui-grid__cell--edit) {
    @apply cursor-pointer;
  }

  &--hovered {
    @apply bg-body-gray;
  }

  &--highlighted {
    @apply bg-primary-light dark:bg-opacity-40;
  }

  &--checkbox {
    @apply absolute top-[0.575rem] -right-1.5;
    @apply z-10;
  }

  &--checkbox + & {
    @apply pr-10;
  }

  &--span-2 {
    @apply col-span-2;
  }

  &[data-selected]:not([data-edit-cell]) {
    @apply before:content-[''];
    @apply before:absolute before:inset-[1px];
    @apply before:border;
    @apply before:border-primary-selected dark:before:border-opacity-40;

    &[data-copy] {
      @apply before:border-dashed;
    }
  }

  &[data-edit-cell] {
    @apply min-w-[7rem];
  }
}

.ui-grid__cell-value {
  @apply relative;
  @apply flex-auto;
  @apply text-sm sm:text-xs;
  @apply text-gray-500 dark:text-gray-400;

  .ui-grid__cell--edit & {
    @apply flex-none;
  }

  &--truncated {
    @apply truncate;
  }

  &--edit {
    @apply -mx-1.5;
  }

  &--clickable {
    @apply underline hover:no-underline;
    @apply underline-offset-2 decoration-gray-400 dark:decoration-gray-500;
    @apply cursor-pointer;
  }
}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__cell {
    @apply min-h-[3.125rem];
    @apply px-2 py-0;
    @apply border-b border-common-border;

    &--first {
      @apply pl-4 sm:pl-default;
    }

    &--last {
      @apply pr-4 sm:pr-default;
    }

    &--checkbox {
      @apply relative top-auto right-auto;
      @apply sticky left-0 z-20;
      @apply bg-grid-cell;

      &.ui-grid__cell--hovered {
        @apply bg-grid-cell-hovered;
      }

      &.ui-grid__cell--highlighted {
        @apply bg-grid-cell-highlighted;
        @apply dark:bg-opacity-40;
      }
    }

    &--checkbox + & {
      @apply pr-2;
    }

    &--span-2 {
      @apply col-auto;
    }

    &--right {
      @apply text-right;
    }
  }

  .ui-grid__cell-value {
    @apply flex-auto;
  }
}

.ui-grid__caption {
  @apply shrink-0;
  @apply text-sm sm:text-xs;
  @apply text-ellipsis overflow-x-hidden;
  @apply text-gray-400 dark:text-gray-500;
}
</style>
